import { useMemo } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code } from '@connectrpc/connect'
import type { SplitSupplyContract } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { AuditPanel } from '~/components/common/AuditPanel'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import { SplitSupplyContractFormFields } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/SplitSupplyContractFormFields'
import { useGetSplitSupplyContract } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useGetSplitSupplyContract'
import { useGetSplitSupplyContractFormOptions } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useGetSplitSupplyContractFormOptions'
import { useUpdateSplitSupplyContract } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useUpdateSplitSupplyContract'
import {
  EMPTY_FORM_OPTIONS,
  convertFormOptions,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/formOptions'
import {
  contractToFormValues,
  emptyFormValues,
  formValuesToRequestFields,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/formValues'
import {
  type SplitSupplyFormValues,
  splitSupplyFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/schema'
import { resolveSplitSupplyContractDetailedError } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/serverError'
import type {
  SplitSupplyContractEditSnapshot,
  SplitSupplyContractRow,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/types'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: SplitSupplyContractRow
  onClose: () => void
  /** 編集モーダルの「削除」。 削除確認へ進む。 */
  onDelete: (snapshot: SplitSupplyContractEditSnapshot) => void
}

/**
 * 分割供給契約の編集モーダル。 供給地点は変更できない (変えたい時は削除して登録し直す)。
 * 今の自社の事業者・相手が候補に無い (失効など) 時は「候補外」として選択欄に残り、
 * 変えなければそのまま保存できる。 自社の事業者は自動で差し替えない。
 */
export const EditSplitSupplyContractModal = ({
  isOpen,
  row,
  onClose,
  onDelete,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateSplitSupplyContract()
  const getQuery = useGetSplitSupplyContract(row.supplyPointSpecificNumber)
  const { isError, error } = getQuery
  const {
    data: formOptionsData,
    isPending: isLoadingFormOptions,
    isError: isFormOptionsError,
    refetch: refetchFormOptions,
  } = useGetSplitSupplyContractFormOptions()
  const options = useMemo(
    () =>
      formOptionsData !== undefined
        ? convertFormOptions(formOptionsData)
        : EMPTY_FORM_OPTIONS,
    [formOptionsData],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    getValues,
    isDirty,
    loadStatus,
    loaded: contract,
  } = useEditModalForm<SplitSupplyContract, SplitSupplyFormValues>({
    source: entitySourceOf(getQuery, (d) => d.splitSupplyContract),
    toFormValues: contractToFormValues,
    schema: splitSupplyFormSchema,
    defaultValues: emptyFormValues(),
    onSubmit: async (values, { expectedUpdatedAt }) => {
      await mutateAsync({
        supplyPointSpecificNumber: row.supplyPointSpecificNumber,
        expectedUpdatedAt,
        ...formValuesToRequestFields(values),
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.splitSupplyContract.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.splitSupplyContract.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.splitSupplyContract.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.splitSupplyContract.serverError.unknown',
    resolveDetailedError: (err, tt) =>
      resolveSplitSupplyContractDetailedError(err, tt),
  })
  const editSnapshot: SplitSupplyContractEditSnapshot | undefined =
    contract === undefined ? undefined : { row, contract }

  const savedBusinesses = useMemo(
    () =>
      contract === undefined
        ? []
        : [contract.loadFollowing, contract.base].flatMap((b) =>
            b === undefined
              ? []
              : [
                  {
                    id: b.businessMasterId,
                    code: b.businessCode,
                    name: b.businessName,
                  },
                ],
          ),
    [contract],
  )

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      loadError={
        isError ? (
          <Alert
            description={mapMasterGetError(
              t,
              error,
              'page:adminOcctoMaster.splitSupplyContract.serverError',
            )}
            status="error"
          />
        ) : undefined
      }
      loadStatus={loadStatus}
      onClose={handleClose}
      onDelete={
        editSnapshot === undefined
          ? undefined
          : () => {
              onDelete(editSnapshot)
            }
      }
      serverError={serverError}
      size="xl"
      title={t('page:adminOcctoMaster.splitSupplyContract.modal.editTitle')}
    >
      {contract !== undefined && (
        <>
          <SplitSupplyContractFormFields
            control={control}
            customerName={contract.customerName}
            errors={errors}
            getValues={getValues}
            isFormOptionsError={isFormOptionsError}
            isLoadingFormOptions={isLoadingFormOptions}
            mode="edit"
            onRetryFormOptions={() => {
              void refetchFormOptions()
            }}
            options={options}
            savedBusinesses={savedBusinesses}
            setValue={setValue}
          />
          <AuditPanel
            createdAt={
              contract.createdAt === undefined
                ? undefined
                : timestampDate(contract.createdAt)
            }
            createdBy={contract.createdBy}
            updatedAt={
              contract.updatedAt === undefined
                ? undefined
                : timestampDate(contract.updatedAt)
            }
            updatedBy={contract.updatedBy}
          />
        </>
      )}
    </CrudModalLayout>
  )
}
